import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Bell, Mail, Save } from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';

// The one place where notifications are set up. In-app notifications (the
// bell) always arrive; the only switch is whether problem summaries also go
// to e-mail. Billing e-mails are sent regardless.
export default function NotificationPreferences() {
  const { t } = useT();
  const [emailOn, setEmailOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/user`);
        if (r?.data && typeof r.data.email_notifications === 'boolean') setEmailOn(r.data.email_notifications);
      } catch (e) {
        toast.error(t('Failed to load notification settings'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/user`, { email_notifications: emailOn });
      toast.success(t('Notification preferences saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="xp-page">
      <XpnsrPageHeader
        icon={Bell}
        title={t('Notifications')}
        subtitle={t('Where notifications appear and what comes by e-mail')}
        cta={
          <Button onClick={save} disabled={saving || loading} data-testid="notifications-save">
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('Saving…') : t('Save preferences')}
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="w-4 h-4" />
              {t('In the interface')}
            </CardTitle>
            <CardDescription>
              {t('Under the bell at the top of the page: received conversions, conversions that could not be sent on, rejected leads, campaigns without clicks and payment problems. They always appear here.')}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-4 h-4" />
              {t('By e-mail')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="email-notifications">{t('Email notifications')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('Summaries of problems (rejected leads and postbacks, no clicks) also come by e-mail. Billing e-mails always arrive.')}
                </p>
              </div>
              <Switch
                id="email-notifications"
                checked={emailOn}
                disabled={loading}
                onCheckedChange={(v) => setEmailOn(!!v)}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
